/* eslint-disable @typescript-eslint/no-unused-vars */
/* eslint-disable no-unused-vars */
import React, { useState, useCallback, useEffect, Fragment } from 'react'
import { Input, Button, Checkbox, Form, message } from 'antd'
import intl from 'react-intl-universal'
import SvgIcon from '@/components/svgIcon'
import AgreementCheckbox from '../agreementCheckbox'
import VerifyCodeInput from '../verifyCodeInput'
import './index.scss'
import { getTokenApi, smsLoginGetEnterpriseList, smsLogin } from '@/services/login'
import initFingerprint from '@/utils/fingerprint'
import { Encrypt } from '@/utils/aes'
import { PwdValidStatus, MFA_VERIFY_TYPE, passwordSecurityLevel, Action } from '@/config/default'
import user from '@/utils/user'
import cache from '@/utils/cache'
import { loginSuccess } from '@/utils/utils'
import { useLoginStore } from '@/store/loginStore'
import { getSMSVerificationCodeApi } from '@/services/common'
import { useRequest } from '@/hooks/useRequest'

const PHONE_REGEX = /^1[3-9]\d{9}$/
const VERIFY_CODE_REGEX = /^\d+$/

function AccountLogin({
  agreementRef,
  agreementChecked,
  setAgreementChecked,
  onAccountTypeChange,
  setShowForgetPassword,
  handleMfaLogin,
  mfaVisible,
  triggerUpdatePassword
}) {
  const [form] = Form.useForm()
  const { validateFields, getFieldValue, resetFields } = form
  const { setShowSelectAccount, setSelectAccounts, setThirdInfo, thirdInfo } = useLoginStore()

  const [activeAccountType, setActiveAccountType] = useState('enterpriseCode') // enterpriseCode 企业号登录 verifyCode 验证码登录
  const [rememberPassword, setRememberPassword] = useState(false)
  const [mobileValue, setMobileValue] = useState('')
  const [loginLoading, setLoginLoading] = useState(false)
  const [formValue, setFormValue] = useState({
    enterpriseCode: '',
    account: '',
    password: '',
    mobile: '',
    verifyCode: ''
  })
  const { runAsync: getToken } = useRequest(getTokenApi, {
    manual: true,
    onBefore: () => setLoginLoading(true),
    onFinally: () => setLoginLoading(false),
  })
  const { runAsync: getEnterpriseList } = useRequest(smsLoginGetEnterpriseList, {
    manual: true,
    onBefore: () => setLoginLoading(true),
    onFinally: () => setLoginLoading(false),
  })

  const accountTypeList = [
    { name: intl.get('login.enterpriseCodeLogin'), value: 'enterpriseCode' },
    { name: intl.get('login.verifyCodeLogin'), value: 'verifyCode' }
  ]

  // 初始化时反显记住的账号信息
  useEffect(() => {
    const remembered = cache.localS('rememberedAccount')
    if (remembered) {
      form.setFieldsValue({
        enterpriseCode: remembered.enterpriseCode,
        account: remembered.account
      })
      setRememberPassword(true)
    }
  }, [])

  const isPhoneValid = mobileValue && PHONE_REGEX.test(mobileValue)

  const handleSendVerifyCode = useCallback(async () => {
    const mobile = getFieldValue('mobile')
    getSMSVerificationCodeApi({
      clientId: 'webapp',
      mobile,
      action: Action.PC_SMS_LOGIN
    })
  }, [getFieldValue])

  const handleAccountTypeChange = (value) => {
    setActiveAccountType(value)
    setMobileValue('')
    resetFields()
    onAccountTypeChange && onAccountTypeChange()
  }

  // 账号登录
  const handleAccountLogin = async () => {
    const doLogin = async (values) => {
      try {
        if (activeAccountType === 'enterpriseCode') {
          try {
            // 企业号登录(常规登录)
            // 获取设备指纹用于mfa验证
            const deviceFingerprint = await initFingerprint({ abbrName: values.enterpriseCode, account: values.account })
            const validDaysMap = JSON.parse(localStorage.getItem('react_mfa_valid_days'))
            const key = `${values.enterpriseCode}_${values.account}`
            const validRandomNum = validDaysMap ? validDaysMap[key] || '' : ''
            const res = await getToken({
              enterpriseCode: values.enterpriseCode,
              account: values.account,
              password: Encrypt(values.password),
              deviceFingerprint: Encrypt(deviceFingerprint + validRandomNum)
            })
            user.stagingUserInfo(res) // 暂存user信息
            if (res.pwdValidStatus && res.pwdValidStatus !== PwdValidStatus.NORMAL) {
              // 用户密码状态异常时，需要强制重置密码
              let securityLevel
              if (res && res.enterprise && res.enterprise.securityLevel) {
                securityLevel = res.enterprise.securityLevel
              }
              triggerUpdatePassword && triggerUpdatePassword({
                updatePsdVisible: true,
                pwdValidStatus: res.pwdValidStatus,
                passwordLevel: user.isSuper() ? passwordSecurityLevel.HIGH : securityLevel,
                passwordLengthMin: res.enterprise.passwordLengthMin || 6,
                passwordLengthMax: res.enterprise.passwordLengthMax || 18
              })
            } else {
              cache.localS('loginCode', values.enterpriseCode)
              const rememberAccount = () => {
                if (rememberPassword) {
                  cache.localS('rememberedAccount', {
                    enterpriseCode: values.enterpriseCode,
                    account: values.account
                  })
                }
              }
              if (res.mfaVerifyStatus === MFA_VERIFY_TYPE.NEED || res.mfaVerifyStatus === MFA_VERIFY_TYPE.NO_BOUND) {
                // 需要MFA验证
                handleMfaLogin(res, rememberAccount)
              } else {
                // 正常登录
                rememberAccount()
                loginSuccess(res)
              }
            }
          } catch (error) {
            console.log(error)
          }
        } else if (activeAccountType === 'verifyCode') {
          // 验证码登录
          try {
            const res = await getEnterpriseList({
              mobile: values.mobile,
              smsCaptcha: values.verifyCode,
              clientId: 'webapp'
            })
            if (res?.enterprises?.length === 0) {
              // 未绑定账号 直接提示请联系管理员
              message.error(intl.get('当前手机未绑定企业账号'))
              return
            } else if (res?.enterprises?.length === 1) {
              // 只绑定了一个账号,直接登录
              const data = await smsLogin({
                mobile: values.mobile,
                enterpriseCode: res.enterprises[0].enterpriseCode,
                smsCaptcha: res.preLoginCaptcha,
              })
              loginSuccess(data)
            } else {
              // 绑定了多个账号
              setShowSelectAccount(true)
              setSelectAccounts(res.enterprises)
              setThirdInfo({
                type: 'SMS',
                mobile: values.mobile,
                preLoginCaptcha: res.preLoginCaptcha
              })
            }
          } catch (error) {
            console.log(error)
          }
        }
      } catch (error) {
        console.log(error)
      }
    }
    try {
      const values = await validateFields()
      // 表单校验通过后，再检查协议是否勾选
      if (!agreementChecked) {
        agreementRef.current && agreementRef.current.showAgreementModal(() => doLogin(values))
      } else {
        doLogin(values)
      }
    } catch (error) {
      console.log(error)
    }
  }

  const enterKeyDown = (e: KeyboardEvent) => {
    if (e.key === 'Enter' && !mfaVisible) {
      e.preventDefault()
      handleAccountLogin()
    }
  }

  useEffect(() => {
    // 监听回车事件
    document.addEventListener('keydown', enterKeyDown)
    // 组件卸载时移除事件监听
    return () => {
      document.removeEventListener('keydown', enterKeyDown)
    }
  }, [agreementChecked, activeAccountType, mfaVisible])

  useEffect(() => {
    if (activeAccountType === 'verifyCode') {
      form.setFieldsValue({
        verifyCode: formValue.verifyCode || '',
        mobile: formValue.mobile || '',
      })
    } else {
      form.setFieldsValue({
        enterpriseCode: formValue.enterpriseCode || '',
        account: formValue.account || '',
        password: formValue.password || '',
      })
    }
  }, [activeAccountType])


  return (
    <div className='loginForm-account'>
      <div className='loginForm-account-type'>
        {
          accountTypeList.map(item => (
            <div
              key={item.value}
              className={`loginForm-account-type-item ${activeAccountType === item.value ? 'active' : ''}`}
              onClick={() => handleAccountTypeChange(item.value)}
            >
              {item.name}
            </div>
          ))
        }
      </div>
      <Form form={form}>
        {
          activeAccountType === 'enterpriseCode' && <Fragment>
            <div className='loginForm-input-wrap'>
              <div className='loginForm-input-text'>{intl.get('login.enterpriseCode')}</div>
              <Form.Item
                name="enterpriseCode"
                rules={[{ required: true, message: intl.get('login.pleaseEnterEnterpriseCode') }]}
              >
                <Input
                  className='loginForm-input'
                  placeholder={intl.get('login.pleaseEnterEnterpriseCode')}
                  prefix={<SvgIcon name='logoEnterprise' />}
                  onChange={(e) => setFormValue({ ...formValue, enterpriseCode: e.target.value.trim() })}
                />
              </Form.Item>
            </div>
            <div className='loginForm-input-wrap'>
              <div className='loginForm-input-text'>{intl.get('login.accountLabel')}</div>
              <Form.Item
                name="account"
                rules={[{ required: true, message: intl.get('login.pleaseEnterAccount') }]}
              >
                <Input
                  className='loginForm-input'
                  placeholder={intl.get('login.pleaseEnterAccount')}
                  prefix={<SvgIcon name='loginAccount' />}
                  onChange={(e) => setFormValue({ ...formValue, account: e.target.value.trim() })}
                />
              </Form.Item>
            </div>
            <div className='loginForm-input-wrap'>
              <div className='loginForm-input-text'>
                <div>{intl.get('login.password')}</div>
                <div className='loginForm-forgot' onClick={() => setShowForgetPassword(true)}>
                  {intl.get('login.forgotPassword')}
                </div>
              </div>
              <Form.Item
                name="password"
                rules={[{ required: true, message: intl.get('login.pleaseEnterPassword') }]}
              >
                <Input.Password
                  className='loginForm-input'
                  placeholder={intl.get('login.pleaseEnterPassword')}
                  prefix={<SvgIcon name='loginPassword' />}
                  onChange={(e) => setFormValue({ ...formValue, password: e.target.value.trim() })}
                />
              </Form.Item>
            </div>
            <div className='loginForm-input-wrap'>
              <Checkbox checked={rememberPassword} onChange={(e) => {
                setRememberPassword(e.target.checked)
                if (!e.target.checked) {
                  cache.localS('rememberedAccount', null)
                }
              }}>{intl.get('login.rememberAccount')}</Checkbox>
            </div>
          </Fragment>
        }
        {
          activeAccountType === 'verifyCode' && <Fragment>
            <div className='loginForm-input-wrap'>
              <div className='loginForm-input-text'>{intl.get('login.phoneNumber')}</div>
              <Form.Item
                name="mobile"
                rules={[
                  { required: true, message: intl.get('login.pleaseEnterPhoneNumber') },
                  { pattern: PHONE_REGEX, message: intl.get('login.pleaseEnterCorrectPhoneNumber') }
                ]}
              >
                <Input
                  className='loginForm-input'
                  placeholder={intl.get('login.pleaseEnterPhoneNumber')}
                  prefix={<SvgIcon name="tel" />}
                  onChange={(e) => {
                    setMobileValue(e.target.value.trim())
                    setFormValue({ ...formValue, mobile: e.target.value.trim() })
                  }}
                />
              </Form.Item>
            </div>
            <div className='loginForm-input-wrap'>
              <div className='loginForm-input-text'>{intl.get('login.verifyCodeLabel')}</div>
              <Form.Item
                name="verifyCode"
                rules={[
                  { required: true, message: intl.get('login.pleaseEnterVerifyCode') },
                  { pattern: VERIFY_CODE_REGEX, message: intl.get('login.pleaseEnterCorrectVerifyCodeFormat') }
                ]}
                getValueFromEvent={(value) => value}
              >
                <VerifyCodeInput
                  disabled={!isPhoneValid}
                  onSend={handleSendVerifyCode}
                  inputClassName='loginForm-input'
                  onChange={(value) => setFormValue({ ...formValue, verifyCode: value })}
                />
              </Form.Item>
            </div>
          </Fragment>
        }
      </Form>
      <AgreementCheckbox
        ref={agreementRef}
        checked={agreementChecked}
        onChange={setAgreementChecked}
      />
      <Button className='loginForm-submit' type='primary' block loading={loginLoading} onClick={handleAccountLogin}>
        {intl.get('login.loginBtn')}
      </Button>
    </div>
  )
}

export default AccountLogin